import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { PaymentProofReview } from "@pages/admin/invoices/PaymentProofReview";
import { invoicesService } from "@/api/invoices";
import { downloadInvoicePDF, openInvoicePDF } from "@/utils/InvoicePDFGenerator";
import { useCredentials } from "@/hooks/useCredentials";
import { useTranslation } from "@/hooks/useTranslation";

const InvoiceDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [isPrinting, setIsPrinting] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Fetch invoice data
    const {
        data: invoice,
        isLoading: invoiceLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => invoicesService.getInvoice(id),
        retry: false,
    });

    // Fetch company credentials
    const {
        data: credentials,
        isLoading: credentialsLoading
    } = useCredentials();

    const isLoading = invoiceLoading || credentialsLoading;

    const getStatusColor = (status) => {
        switch (status) {
            case "PAID": return "bg-green-100 text-green-800";
            case "SUBMITTED": return "bg-yellow-100 text-yellow-800";
            case "REJECTED": return "bg-red-100 text-red-800";
            case "REFUNDED": return "bg-blue-100 text-blue-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "PAID": return t("invoices.common.statusPaid");
            case "SUBMITTED": return t("invoices.admin.statusSubmitted");
            case "REJECTED": return t("invoices.common.statusRejected");
            case "REFUNDED": return t("invoices.common.statusRefunded");
            default: return status || t("invoices.common.statusPending");
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    const handleDownloadPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await downloadInvoicePDF(invoice, `Facture-${invoice.id}.pdf`);
        } catch (error) {
            console.error("Failed to generate PDF:", error);
            alert(t("invoices.common.pdfGenerateError"));
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePreviewPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await openInvoicePDF(invoice);
        } catch (error) {
            console.error("Failed to preview PDF:", error);
            alert(t("invoices.common.pdfOpenError"));
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!invoice) return;

        try {
            await invoicesService.updateStatus(invoice.id, newStatus);
            refetch();
            alert(t("invoices.common.statusUpdatedTo", { status: getStatusText(newStatus) }));
        } catch (error) {
            console.error("Failed to update status:", error);
            alert(t("invoices.common.statusUpdateError"));
        }
    };

    // Calculate totals for display
    const calculateTotals = () => {
        if (!invoice) return { subtotal: 0, totalDiscount: 0, afterDiscount: 0, total: 0 };

        const items = invoice.items || [];
        const subtotal = items.reduce((sum, item) => {
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const quantity = parseInt(item.quantity) || 0;
            return sum + (unitPrice * quantity);
        }, 0);

        const totalDiscount = items.reduce((sum, item) => {
            return sum + (parseFloat(item.discountValue) || 0);
        }, 0);

        const afterDiscount = subtotal - totalDiscount;
        const taxAmount = parseFloat(invoice.tax) || 0;
        const total = afterDiscount + taxAmount;

        return { subtotal, totalDiscount, afterDiscount, taxAmount, total };
    };

    useEffect(() => {
        const handleAfterPrint = () => {
            setIsPrinting(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    if (!invoice) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <h2 className="text-2xl font-bold mb-4">{t("invoices.common.notFound")}</h2>
                <TaleButton onClick={() => navigate("/admin/factures")}>
                    {t("invoices.common.backToInvoicesList")}
                </TaleButton>
            </div>
        );
    }

    const { subtotal, totalDiscount, afterDiscount, taxAmount, total } = calculateTotals();

    // Helper function to split address lines for display
    const getAddressLines = (address) => {
        if (!address) return [];
        return address.split(',').map(line => line.trim());
    };

    return (
        <div className={`p-6 ${isPrinting ? "print-mode" : ""}`}>
            {/* Print styles */}
            <style>
                {`
          @media print {
            .no-print { display: none !important; }
            .print-mode * { color: black !important; }
            .print-container { 
              box-shadow: none !important; 
              border: 1px solid #ccc !important;
            }
            .print-table {
              border-collapse: collapse;
              width: 100%;
            }
            .print-table th, .print-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .print-table th {
              background-color: #f2f2f2;
            }
          }
        `}
            </style>

            {/* Invoice Content */}
            <TaleDashbaordMainCard className="print-container">
                <div className="no-print mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-3xl font-bold">{t("invoices.common.invoiceNumber", { id: invoice.id })}</h1>
                            <div className="flex items-center gap-4 mt-2">
                                <span className={`px-3 py-1 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <span className="text-gray-600">
                                    {t("invoices.common.createdOn", { date: formatDate(invoice.creationDate) })}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <TaleButton onClick={() => navigate("/admin/factures")}>
                                <i className="fas fa-arrow-left mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.common.back")}
                            </TaleButton>
                            <TaleButton
                                variant="outlined"
                                onClick={() => navigate(`/admin/factures/${id}/edit`)}
                            >
                                <i className="fas fa-edit mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("common.edit")}
                            </TaleButton>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                        <TaleButton
                            onClick={handleDownloadPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-download mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.common.downloadPDF")}
                        </TaleButton>

                        <TaleButton
                            variant="outlined"
                            onClick={handlePreviewPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-eye mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.common.previewPDF")}
                        </TaleButton>

                        {/* Status Update Buttons */}
                        {invoice.status === "SUBMITTED" && (
                            <TaleButton
                                variant="outlined"
                                className="!bg-green-50 !text-green-700"
                                onClick={() => handleStatusChange("PAID")}
                            >
                                <i className="fas fa-check-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.admin.markPaid")}
                            </TaleButton>
                        )}

                        {invoice.status === "SUBMITTED" && (
                            <TaleButton
                                variant="outlined"
                                className="!bg-red-50 !text-red-700"
                                onClick={() => handleStatusChange("REJECTED")}
                            >
                                <i className="fas fa-times-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.admin.reject")}
                            </TaleButton>
                        )}

                        {invoice.status === "PAID" && (
                            <TaleButton
                                variant="outlined"
                                className="!bg-blue-50 !text-blue-700"
                                onClick={() => handleStatusChange("REFUNDED")}
                            >
                                <i className="fas fa-undo mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.common.refund")}
                            </TaleButton>
                        )}
                    </div>

                    <PaymentProofReview invoice={invoice} onReviewed={refetch} />
                </div>

                {/* Header - Dynamic with credentials */}
                <div className="flex justify-between mb-8 pb-6 border-b">
                    <div>
                        <h2 className="text-2xl font-bold">{t("invoices.common.invoiceHeaderWord")}</h2>
                        <p className="text-gray-600 font-medium">{credentials?.companyName || t("invoices.common.yourCompany")}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index} className="text-gray-600">{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p className="text-gray-600">{t("invoices.common.tel", { phone: credentials.phoneNumber })}</p>
                        )}

                        {credentials?.taxId && (
                            <p className="text-gray-600">{t("invoices.common.taxIdLine", { taxId: credentials.taxId })}</p>
                        )}
                    </div>

                    <div className="text-right rtl:text-left">
                        <div className="text-3xl font-bold mb-2">{t("invoices.common.invoiceNumber", { id: invoice.id })}</div>
                        <div className="text-gray-600">
                            {t("invoices.common.issueDateLine", { date: formatDate(invoice.creationDate) })}
                        </div>
                        {invoice.dueDate && (
                            <div className="text-gray-600">
                                {t("invoices.common.dueDateLine", { date: formatDate(invoice.dueDate) })}
                            </div>
                        )}
                    </div>
                </div>

                {/* From/To - Dynamic with credentials */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    {/* Émetteur - Dynamic */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">{t("invoices.common.issuer")}</h3>
                        <p className="font-bold">{credentials?.companyName || t("invoices.common.yourCompany")}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index}>{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p>{t("invoices.common.tel", { phone: credentials.phoneNumber })}</p>
                        )}

                        {credentials?.taxId && (
                            <p>{t("invoices.common.taxIdLine", { taxId: credentials.taxId })}</p>
                        )}
                    </div>

                    {/* Client - Dynamic from invoice */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">{t("invoices.common.client")}</h3>
                        {invoice.company ? (
                            <>
                                <p className="font-bold">{invoice.company.companyName}</p>
                                {invoice.company.address && (
                                    getAddressLines(invoice.company.address).map((line, index) => (
                                        <p key={index}>{line}</p>
                                    ))
                                )}
                            </>
                        ) : invoice.user ? (
                            <>
                                <p className="font-bold">{invoice.user.firstname} {invoice.user.lastname}</p>
                                <p>{invoice.user.email}</p>
                                {invoice.user.address && (
                                    <p>{invoice.user.address}</p>
                                )}
                                {invoice.user.phoneNumber && (
                                    <p>{t("invoices.common.tel", { phone: invoice.user.phoneNumber })}</p>
                                )}
                            </>
                        ) : (
                            <p className="text-gray-500">{t("invoices.common.noClientInfo")}</p>
                        )}
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <table className="w-full border-collapse print-table">
                        <thead>
                        <tr className="bg-gray-50">
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.itemHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.quantity")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.unitPriceHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountPercentTh")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountAmountHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.totalHeader")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(invoice.items || []).map((item, index) => {
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const quantity = parseInt(item.quantity) || 0;
                            const discountPercentage = parseFloat(item.discountPercentage) || 0;
                            const discountValue = parseFloat(item.discountValue) || 0;
                            const itemTotal = (unitPrice * quantity) - discountValue;

                            return (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="border p-3">{item.name}</td>
                                    <td className="border p-3 text-center">{quantity}</td>
                                    <td className="border p-3">{formatCurrency(unitPrice)}</td>
                                    <td className="border p-3 text-center">{discountPercentage}%</td>
                                    <td className="border p-3">{formatCurrency(discountValue)}</td>
                                    <td className="border p-3 font-semibold">
                                        {formatCurrency(itemTotal)}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* Summary */}
                <div className="flex justify-end">
                    <div className="w-64">
                        <div className="flex justify-between mb-2">
                            <span>{t("invoices.common.subtotalLabel")}</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>

                        {totalDiscount > 0 && (
                            <>
                                <div className="flex justify-between mb-2 text-green-600">
                                    <span>{t("invoices.common.totalDiscountLabel")}</span>
                                    <span>-{formatCurrency(totalDiscount)}</span>
                                </div>
                                <div className="flex justify-between mb-2 pt-2 border-t">
                                    <span>{t("invoices.common.amountAfterDiscount")}</span>
                                    <span className="font-semibold">{formatCurrency(afterDiscount)}</span>
                                </div>
                            </>
                        )}

                        <div className="flex justify-between mb-2">
                            <span>{t("invoices.common.vatLabel")}</span>
                            <span>{formatCurrency(taxAmount)}</span>
                        </div>

                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                            <span>{t("invoices.common.totalTTCLabel")}</span>
                            <span>{formatCurrency(total)}</span>
                        </div>
                    </div>
                </div>

                {/* Status and Payment Details */}
                <div className="mt-8 pt-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg mb-3">{t("invoices.common.statusLabel")}</h3>
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-2 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <span className="text-sm text-gray-600">
                                    {invoice.status === "SUBMITTED" && t("invoices.admin.awaitingPayment")}
                                    {invoice.status === "PAID" && t("invoices.common.paidOn", { date: formatDate(invoice.paymentDate) })}
                                    {invoice.status === "REJECTED" && t("invoices.common.invoiceRejected")}
                                    {invoice.status === "REFUNDED" && t("invoices.common.statusRefunded")}
                                </span>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-lg mb-3">{t("invoices.common.paymentDetails")}</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p><strong>{t("invoices.common.paymentMethodLabel")}</strong> {invoice.paymentMethod || t("invoices.common.bankTransfer")}</p>
                                <p><strong>{t("invoices.common.ribLabel")}</strong> FR76 1234 5678 9012 3456 7890 123</p>
                                <p><strong>{t("invoices.common.bicLabel")}</strong> ABCD1234567</p>
                                <p className="text-sm text-gray-600 mt-2">
                                    {t("invoices.admin.transferNote", { id: invoice.id })}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer - Dynamic with credentials */}
                <div className="mt-10 pt-6 border-t text-center text-gray-600 text-sm">
                    <p className="font-medium">{credentials?.companyName || t("invoices.common.yourCompany")}</p>

                    {credentials?.address && (
                        <p>{t("invoices.common.addressLine", { address: credentials.address })}</p>
                    )}

                    {credentials?.taxId && (
                        <p className="mt-2">{t("invoices.common.vatIntraLine", { taxId: credentials.taxId })}</p>
                    )}

                    {!credentials?.companyName && !credentials?.address && !credentials?.taxId && (
                        <p className="text-gray-400">
                            {t("invoices.admin.configureCompanyInfo")}
                        </p>
                    )}
                </div>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default InvoiceDetail;
